/**
 * opportunities/editOpportunity/headerValues.jsx - what the hero header
 * shows (account, close date, revenue, the quoted cards, pills, the actuals
 * band, KSA costs) and which tabs the stage has.
 * Moved verbatim out of EditOpportunityModal.jsx; the editor passes in the
 * values it reads.
 */
import { Pill } from '../../ui/Pill';
import FinancePanel from '../../shared/FinancePanel';
import ActiveClosedPill from '../../projects/ActiveClosedPill';
import { canSeeTab, hasArea } from '../../../data/roles';
import { fmt } from '../../../utils/format';
import { editorTabs } from '../EditOpportunityHeader';
import KsaCostsPanel from '../../ksa/KsaCostsPanel';
import { fxCode, fmtMoney } from '../../ksa/ksaMoney';

export function makeHeaderValues({
  f,
  scope,
  anticipatedValue,
  price,
  totalCogs,
  quotedProfit,
  grossMarginPct,
  marginColor,
  opp,
  isCreate,
  editable,
  isProject,
  familyLabel,
  access,
  tab,
}) {
  // Meta header values
  const metaAccount = f.customer || '-';
  const metaEstClose = f.estCloseDate || '-';
  // KSA money is in the record's own currency: label it with the code, not a $
  const ksaCurrency = scope ? fxCode(f.currency) || 'SAR' : null;
  const money = ksaCurrency ? (v) => fmtMoney(v, ksaCurrency) : fmt;
  const metaEstRevenue = anticipatedValue > 0 ? money(anticipatedValue) : '-';
  // a project's cards are what was quoted (what we bid); its actuals sit under them
  const projectCards = [
    { label: 'Quoted Price', value: price ? money(price) : '-', color: '#0D0D0D', mono: true },
    { label: 'Quoted COGS', value: totalCogs ? money(totalCogs) : '-', color: '#B71C1C', mono: true },
    { label: 'Quoted Profit', value: quotedProfit && totalCogs ? money(quotedProfit) : '-', color: '#1B5E20', mono: true },
    { label: 'Quoted Margin', value: grossMarginPct != null ? `${grossMarginPct.toFixed(2)}%` : '-', color: marginColor },
  ];
  // the KSA costs and profit work from the SAVED record; say so when the quote was changed here
  const savedNum = (a, b) => Number(opp[a] ?? opp[b]) || 0;
  const quoteUnsaved =
    !isCreate &&
    !!scope &&
    ((parseFloat(f.quotedPrice) || 0) !== savedNum('quotedPrice', 'quoted_price') ||
      (parseFloat(f.quotedLaborCost) || 0) !== savedNum('quotedLaborCost', 'quoted_labor_cost') ||
      (parseFloat(f.quotedExpenses) || 0) !== savedNum('quotedExpenses', 'quoted_expenses') ||
      f.currency !== (opp.currency || 'SAR') ||
      f.quoteDate !== (opp.quoteDate || opp.quote_date || ''));
  const ksaCosts = (
    <>
      {quoteUnsaved && (
        <div
          style={{
            marginBottom: 8,
            padding: '6px 10px',
            background: '#FFF8E1',
            border: '1px solid #FDE68A',
            borderRadius: 6,
            fontSize: 11.5,
            color: '#92400E',
            fontWeight: 600,
          }}
        >
          The quote has unsaved changes. The figures below use the saved quote until you save.
        </div>
      )}
      <KsaCostsPanel project={opp} editable={editable} />
    </>
  );
  // A project's actuals band: the finance figures (main) or the KSA costs and
  // profit (purchases in any currency, profit in the project's) - where the
  // project page had them, under the quoted cards.
  const actuals =
    isProject && !isCreate ? (
      <div style={{ marginTop: 12 }}>{scope ? ksaCosts : <FinancePanel projectId={opp.id} project={opp} />}</div>
    ) : null;
  const pills = isProject ? (
    <>
      <Pill label={f.status} />
      <ActiveClosedPill value={f.closed === 'YES' ? 'Closed' : 'Active'} />
    </>
  ) : null;
  // "Project created from won opportunity OPP-12 ..." - the old auto-made projects say where they came from
  const fromMatch = !isCreate && String(opp.description || '').match(/^Project created from won opportunity (OPP-\d+|dyn-\d+)/);
  const tabs = editorTabs({
    isCreate,
    isProject,
    showTimeline: !scope,
    showCosts: !!scope && !isProject,
    familyLabel,
    // the Tracking tab (10/14/26): a main-tab project; cost inside it for 'cost-rates' only
    showProjectCosts: !scope && isProject && canSeeTab(access, 'projects'),
    // Daily reports: an AI feature, Sharif's alone until he releases it (11/2/26)
    showDailies: !scope && isProject && hasArea(access, 'ai-preview', 'view'),
  });
  // a tab the stage no longer has (Costs after picking Won...) falls back to the Summary
  const shown = tabs.some(([id]) => id === tab) ? tab : 'summary';
  // read only without the area's Edit: the inputs are disabled, nothing to save
  const lockFields = (node) =>
    editable ? (
      node
    ) : (
      <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        {node}
      </fieldset>
    );
  return {
    shown,
    metaAccount,
    metaEstClose,
    metaEstRevenue,
    projectCards,
    pills,
    fromMatch,
    actuals,
    tabs,
    lockFields,
    ksaCosts,
    ksaCurrency,
  };
}
